import { useCallback, useEffect, useRef, useState } from 'react';
import { useDependency } from '../../../contexts/DependencyProvider';
import { useViewInfo } from '../../../hooks/UseViewInfo';
import { statusColor } from '../../../utils/helperUtil';
import useBrowserName from '../../../hooks/UseBrowserName';
import useScreenSize from '../../../hooks/UseScreenSize';
import moment from 'moment';
import { ToastQueue } from '@react-spectrum/toast';
import { useAsyncList } from 'react-stately';
import {
  Flex,
  View,
  ProgressCircle,
  Grid,
  TableView,
  TableBody,
  TableHeader,
  Row,
  Cell,
  Column,
  Heading,
  Content,
  ListBox,
  Item,
  IllustratedMessage,
  Link,
  DialogContainer,
} from '@adobe/react-spectrum';
import { CircleFill } from 'react-bootstrap-icons';
import { FinderIcon } from '../../../components/protected/Icons/IconsLib';
import NotFound from '@spectrum-icons/illustrations/NotFound';
import FilterComponent from './component/FilterComponent';
import HeaderComponent from './component/HeaderComponent';
import { invokeFinderApp } from '../../../utils/NavigationUtils';
import { RootState } from '../../../state/store';
import { ListProjectAdvancedRequest } from '../../../services/soap/project/requests/ListProjectAdvancedRequest';
import { ListStatusRequest, ListStatusType } from '../../../services/soap/project/requests/ListStatusRequest';
import { LayoutDefinition } from '../../../infra/protected/Layout/LayoutDefinitions';
import { GetFormPanelRequest } from '../../../services/soap/features/requests/GetFormPanelRequest';
import { SoapUtils } from '../../../utils/SoapUtils';
import { ListFormSettingsRequest } from '../../../services/soap/features/requests/ListFormSettingsRequest';
import { GetFormSettingsRequest } from '../../../services/soap/features/requests/GetFormSettingsRequest';
import { ListCurrencyRequest } from '../../../services/soap/global/requests/ListCurrencyRequest';
import { ListRegisterRequest } from '../../../services/soap/features/requests/ListRegisterRequest';
import CardComponent from './component/CardComponent';
import { ComponentFactory } from '../../../components/protected/layout-components/ComponentFactory';
import { FPAData, FPADataFactory } from '../../../infra/protected/FPA/FPAData';
import { usePreloadAssets } from '../../../hooks/UsePreloadAssets';
import FilterSearchTextBox from './component/FilterSearchTextBox';
import useNumberFormatter from '../../../i18n/useNumberFormatter';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { updateAppInfo } from '../../../state/userWebLayout/userWebLayoutSlice';
import styles from './project_activities.module.css';
import { get } from 'http';
import { DataProvider } from '../../../components/protected/data-components/Contexts/DataContext';
import { ContextNewDialog } from '../../../components/protected/Context/ContextNewDialog';
import { addKey, useKeyboardShortcuts } from '../../../hooks/UseKeyboardShortcuts';

interface Character {
  id: string;
  name: string;
}

export function ProjectActivities() {
  const { t } = useTranslation();
  const browserName = useBrowserName();
  const screenSize = useScreenSize();
  const { isMobile } = useViewInfo();
  const { activityStatusMapping } = usePreloadAssets();
  const { store, projectService, featureService, globalService } = useDependency();
  const dataStore = useSelector((state: any) => state.userWebLayout.appInfos);
  const selectedApp = useSelector((state: RootState) => state.userWebLayout.selectedAppId);
  const dispatch = useDispatch();
  const { state } = useLocation();
  const [showLoader, setShowLoader] = useState(true);
  const [showLoaderDetail, setShowLoaderDetail] = useState(true);
  const [rowsData, setRowsData] = useState<any>([]);
  const [columnsData, setColumnsData] = useState<any>([]);
  const [pageView, setPageView] = useState<string>('Grid');
  const [showAdd, setShowAdd] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<any>>();
  const [projectStatusList, setProjectStatusList] = useState<any>([]);
  const [definition, setDefinition] = useState<LayoutDefinition | null>(null);
  const [showMobileDetailView, setShowMobileDetailView] = useState<boolean>(false);
  const [stopLoadData, setStopLoadData] = useState<boolean>(false);
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [selectedItem, setSelectedItem] = useState<FPAData | null>(null);
  const [selectedDataRow, setSelectedDataRow] = useState<any>({});
  const projectActivityValue = useRef<string>('');
  const currencyList = useRef<any>([]);
  const queryParams = useRef<any>({ FILTER: { COL: [] } });
  const totalProjectListRec = useRef<number>(0);
  const offSetLimit = useRef<number>(0);
  const dataRef = useRef<any>({});
  const formatNumber = useNumberFormatter();
  const PAGE_SIZE = 50;

  useEffect(() => {
    if (state.appKey && dataStore[state.appKey]) {
      if (dataStore[state.appKey].pageView) {
        dataRef.current.pageView = dataStore[state.appKey].pageView;
        setPageView(dataStore[state.appKey].pageView);
        return;
      }
    }
  }, [dataStore.appInfos, state.appKey]);

  useKeyboardShortcuts([
    ...addKey('c', {ctrl: true, shift: true}, () => onAddNew())
  ],true);
  useEffect(() => {
    (async () => {
      queryParams.current = { FILTER: { COL: [] } };
      projectActivityValue.current = '';
      setSearchedValue('');
      Promise.all([getCurrencyList(), getProjectStatusArray()]);
    })();
  }, [selectedApp]);

  useEffect(() => {
    setShowLoader(true);
    setShowLoaderDetail(true);
    setPageView('Grid');
  }, [selectedApp]);

  useEffect(() => {
    return () => {
      dispatch(
        updateAppInfo({
          key: state.appKey,
          data: {
            pageView: dataRef.current.pageView,
          },
        })
      );
    };
  }, []);

  useEffect(() => {
    if (selectedApp !== '') {
      listProjectList.reload();
    }
  }, [selectedApp]);

  const getCurrencyList = async () => {
    let resCurrencyList = await globalService.listCurrency(new ListCurrencyRequest(store.Server, store.SessionId, 50, 0));
    if (resCurrencyList?.result === 'OK') {
      const formattedArray = resCurrencyList.ROWS.map(({ ITEM }: { ITEM: { id: string; code: string } }) => ({
        id: ITEM.id,
        code: ITEM.code,
      }));
      currencyList.current = formattedArray;
    }
  };

  const getFormSettings = useCallback(
    async (settingId: string) => {
      //console.log('settingId', settingId);
      if (settingId === '') {
        const resFormSettings = await featureService.listFormSettings(new ListFormSettingsRequest(store.Server, store.SessionId));
        let filteredData = resFormSettings.SETTING.filter((item: any) => item.formId === 'ListProjectAdvanced' && item.isDefault == '1');
        if (filteredData.length > 0) {
          settingId = filteredData[0].id.toString();
        }
      }

      const response = await featureService.getFormSettings(new GetFormSettingsRequest(store.Server, store.SessionId, settingId));
      if (response?.result === 'OK' && response?.count > 0) {
        //console.log('response', response);
        const filteredData = response.SETTING[0]?.ITEM?.filter(item => String(item.visibility) === '1');
        const result = filteredData?.map(item => {
          const key = item.itemname.includes('|') ? item.itemname.split('|')[1] : item.itemname;
          const name = item.caption && item.caption.trim() !== '' ? item.caption : item.name;
          return { key, name };
        });
        setColumnsData(result);
      }
    },
    [selectedApp]
  );

  const getProjectStatusArray = useCallback(async () => {
    const statusArray: any = [];
    let res: any = await projectService.listProjectStatus(new ListStatusRequest(store.Server, store.SessionId, ListStatusType.Project, projectActivityValue.current));

    if (res.result === 'OK' && res.STATUS_LIST.length > 0) {
      res?.STATUS_LIST.forEach((status: any) => {
        // Extract id and name from each STATUS object
        const { id, name, color } = status.STATUS;
        let colorString = statusColor(color);
        // Push an object containing id and name to the statusArray
        statusArray.push({ id, name, color: colorString });
      });
      //console.log('statusArray', statusArray);
      statusArray.unshift({ id: 'All states', name: 'All states', color: 'black', types: [] });
      setProjectStatusList(statusArray);
    }
  }, []);

  const getProjectListKey = useCallback(async () => {
    //console.log('selectedApp', selectedApp);
    let resListRegister = await featureService.listRegister(new ListRegisterRequest(store.Server, store.SessionId, undefined, undefined, undefined, undefined, +selectedApp));
    //console.log('resListRegister', resListRegister);
    projectActivityValue.current = resListRegister?.ROWS?.find((row: any) => row.ROW.key === 'FPATypeCategoryForListProject')?.ROW.value ?? '';

    let settingId = resListRegister?.ROWS?.find((row: any) => row.ROW.key === 'formSetting')?.ROW.value ?? '';

    await getFormSettings(settingId);

    return resListRegister?.ROWS?.find((row: any) => row.ROW.key === 'FPATypeCategoryForListProject')?.ROW.value ?? '';
  }, [selectedApp]);

  const getProjectListCount = async () => {
    //console.log('object', filterParams?.FILTER);

    const fpaTypeCategoryExists = queryParams.current.FILTER.COL.some((col: any) => col.name === 'fpaTypeCategory');
    if (!fpaTypeCategoryExists) {
      queryParams.current.FILTER.COL.push({
        name: 'fpaTypeCategory',
        type: 'any',
        value: projectActivityValue.current !== '' ? projectActivityValue.current : await getProjectListKey(),
      });
    }

    // Remove duplicates in the FILTER.COL array
    const uniqueCOL: any = [];
    const typeName = new Set();

    queryParams.current.FILTER.COL.forEach((item: any) => {
      if (!typeName.has(item.name)) {
        typeName.add(item.name);
        uniqueCOL.push(item);
      }
    });

    // Update the COL array with unique values
    queryParams.current.FILTER.COL = uniqueCOL;

    let projectRes = await projectService.listProjectAdvanced(
      new ListProjectAdvancedRequest(store.Server, store.SessionId, {
        countOnly: '1',
        FILTER: queryParams.current?.FILTER,
      })
    );
    if (projectRes?.result == 'OK') {
      totalProjectListRec.current = projectRes.count != 0 ? projectRes.count : 0;
      return projectRes.count != 0 ? projectRes.count : 0;
    }
  };

  const loadProjectActivities = async () => {
    try {
      //console.log('object', filterParams?.FILTER);
      if (stopLoadData) {
        return rowsData;
      }
      setSearchedValue('');
      let projectRes = await projectService.listProjectAdvanced(
        new ListProjectAdvancedRequest(store.Server, store.SessionId, {
          limit: PAGE_SIZE,
          offset: offSetLimit.current ?? 0,
          FILTER: queryParams.current?.FILTER,
          ORDER: {
            COL: [
              {
                name: 'modified',
                desc: 'true',
              },
            ],
          },
        })
      );
      //console.log('projectRes)', projectRes);
      if (projectRes?.result == 'OK' && projectRes?.count > 0) {
        let listProject: any = projectRes?.PROJECTS?.PROJECT;
        listProject.forEach((item: any) => {
          item.chargeable = item.chargeable === '1' ? 'Yes' : 'No';
          const currency = currencyList.current.find((currency: any) => currency.id == item.currency);
          if (item.amountWithoutVat) {
            item.amountWithoutVat = currency ? currency.code + ' ' + formatNumber(Number(item.amountWithoutVat?.split('.')[0].replace(/\D/g, ''))) : item.amountWithoutVat;
          } else {
            item.amountWithoutVat = 0;
          }
        });
        setRowsData(listProject);
        //console.log('listProject', listProject);
        if (listProject.length > 0) {
          setSelectedKeys(new Set([listProject[0].id]));
          setSelectedDataRow(listProject[0]);
          getLayout(listProject[0]);
        }
        return listProject;
      } else {
        setRowsData([]);
        //ToastQueue.info(t('no_results', { ns: 'layout_components' }), { timeout: 50 });
        return [];
      }
    } finally {
      setShowLoader(false);
    }
  };

  let listProjectList = useAsyncList<Character>({
    async load({ cursor }) {
      //console.log('cursor', cursor);

      if (selectedApp === '') {
        setShowLoader(false);
        return { items: rowsData, cursor: undefined };
      }

      if (cursor && Number(cursor) * PAGE_SIZE >= totalProjectListRec.current) {
        setShowLoader(false);
        return { items: rowsData, cursor: undefined };
      }
      let totalRecords = await getProjectListCount();
      totalRecords = totalRecords ? Number(totalRecords) : 0;
      //console.log('totalRecords', totalRecords);
      let currentPage = cursor ? parseInt(cursor) : 0;
      //console.log('currentPage', currentPage);
      let offSet = PAGE_SIZE * currentPage;
      //console.log('offset', offSet);
      offSetLimit.current = offSet;
      let itemList = await loadProjectActivities();
      //console.log('itemList', itemList);
      return {
        items: itemList,
        cursor: String(offSet >= totalRecords ? undefined : currentPage + 1),
      };
    },
  });

  const reloadProjectActivities = useCallback((filterParams: any) => {
    //console.log('reloadProjectActivities Called', filterParams);
    setStopLoadData(false);
    queryParams.current = filterParams;
    totalProjectListRec.current = 0;
    offSetLimit.current = 0;
    listProjectList.reload();
  }, []);

  const handleTableRowClick = (rowId: any): void => {
    invokeFinderApp(rowId);
  };

  const handleListRowClick = (rowId: any): void => {
    if (selectedKeys?.has(rowId.currentKey)) {
      return;
      setSelectedDataRow({});
      setSelectedKeys(undefined);
      setDefinition(null);
    } else {
      setShowLoaderDetail(true);
      setSelectedKeys(rowId);
      setPageView('Full');
      dataRef.current.pageView = 'Full';
      let selectedRow = rowsData.find((item: any) => item.id === rowId.currentKey);
      if (!selectedRow) {
        setSelectedDataRow({});
        setSelectedKeys(undefined);
        setDefinition(null);
        return;
      }
      setSelectedDataRow(selectedRow);
      getLayout(selectedRow);
    }
  };

  const handleRowClickMobile = (rowId: any): void => {
    const nameSearchExists = queryParams.current.FILTER.COL.find((col: any) => col.name === 'nameSearch');
    if (nameSearchExists) {
      setSearchedValue(nameSearchExists.value);
    }
    setSelectedKeys(undefined);
    let selectedRow = rowsData.find((item: any) => item.id === rowId.currentKey);
    if (!selectedRow) {
      setSelectedDataRow({});
      setSelectedKeys(undefined);
      setDefinition(null);
      return;
    }
    setSelectedDataRow(selectedRow);
    getLayout(selectedRow);
    setShowMobileDetailView(true);
  };

  const handleViewChange = (view: string) => {
    setPageView(view);
    dataRef.current.pageView = view;
  };

  const getStatusColor = (statusName: any) => {
    const statusObj = projectStatusList.find((obj: any) => obj.name === statusName);
    return (
      <>
        <CircleFill style={{ paddingRight: '10px' }} size={'18px'} color={statusObj?.color} /> {statusName}
      </>
    );
  };

  const addStatusColor = (statusName: any) => {
    const statusObj = projectStatusList.find((obj: any) => obj.name === statusName);
    return statusObj?.color;
  };

  const getLayout = async (selectedRow: any) => {
    try {
      let resSelectedItem = FPADataFactory.createFromProjectAdvanced(selectedRow, activityStatusMapping);
      setSelectedItem(resSelectedItem);
      let typeId = selectedRow.projectType;
      const layout = await featureService.getFormPanel(new GetFormPanelRequest(store.Server, store.SessionId, '' + typeId));

      if (layout.count > 0) {
        let scheme = SoapUtils.parseXmlString2(layout.ROW.SCHEME);
        let layoutObject = JSON.parse(scheme.ActivityPanelSettings.layoutObject);
        setDefinition(layoutObject);
      } else {
        setDefinition(null);
      }
    } catch (err) {
      console.error('getlayout error: ', err);
    } finally {
      setShowLoaderDetail(false);
    }
  };

  const Loader = () => (
    <Flex width="100%" justifyContent="center" marginTop={10}>
      <ProgressCircle aria-label="Loading…" isIndeterminate />
    </Flex>
  );

  const NoResultsMessage = () => (
    <Flex direction="row" width="100%" alignContent="center" justifyContent="center">
      <IllustratedMessage>
        <NotFound />
        <Heading>{t('no_results', { ns: 'layout_components' })}</Heading>
        <Content>{t('try_another_search', { ns: 'layout_components' })}</Content>
      </IllustratedMessage>
    </Flex>
  );

  const getHeight = (baseHeight: number) => {
    // if (browserName === 'Chrome') {
    //   return `${baseHeight - 120}px`;
    // } else if (browserName === 'Safari') {
    //   return `${baseHeight - 120}px`;
    // } else {
    //   return `${baseHeight - 120}px`;
    // }
    return `${baseHeight - 120}px`;
  };

  const getWidth = () => {
    // if (browserName === 'Chrome') {
    //   return `95%`;
    // } else if (browserName === 'Safari') {
    //   return `95%`;
    // } else {
    //   return `95%`;
    // }
    return `95%`;
  };

  const detailStyle = {
    height: getHeight(screenSize.height - 20),
  };

  const cardStyle = {
    height: getHeight(screenSize.height),
    width: getWidth(),
  };

  const filterStyle = {
    height: getHeight(screenSize.height + 60),
    overflow: 'hidden auto',
  };

  const onAddNew = () => {
    // ToastQueue.info('Under Development Project', { timeout: 10 });
    setShowAdd(true);
  };

  const handleAddNewClose = (reload_flag: boolean) => {
    if (reload_flag) listProjectList.reload();

    setShowAdd(false);
  };
  const handleAddNewCloseDismiss = () => {
    setShowAdd(false);
  };
  return (
    <>
      {isMobile || window.innerWidth < 1030 ? (
        <>
          {!showMobileDetailView && (
            <>
              <FilterComponent onFilterChange={reloadProjectActivities} filterParams={queryParams.current} />
              <View position={'relative'} width={'100%'} height={'100%'} UNSAFE_className={styles.card_list_parent}>
                <Flex direction={'column'} position={'absolute'} width={'100%'} top={'72px'} left={'0%'} alignItems={'start'} justifyContent={'start'} gap={'12px'}>
                  {showLoader ? (
                    <Loader />
                  ) : (
                    <ListBox
                      id="listActivityCardsMobile"
                      selectionMode="single"
                      onSelectionChange={handleRowClickMobile}
                      marginStart={'18px'}
                      maxHeight={'100vh'}
                      minHeight={'130px'}
                      defaultSelectedKeys={selectedKeys}
                      aria-label="Project Activities Cards Mobile"
                      items={listProjectList.items}
                      isLoading={listProjectList.isLoading}
                      width={'90%'}
                    >
                      {item => (
                        <Item key={item.id}>
                          <CardComponent item={item} projectStatusList={projectStatusList} />
                        </Item>
                      )}
                    </ListBox>
                  )}
                </Flex>
                <Flex
                  direction={'column'}
                  position={'absolute'}
                  width={'calc(100% - 32px)'}
                  top={'16px'}
                  right={'16px'}
                  left={'16px'}
                  alignItems={'start'}
                  justifyContent={'start'}
                  UNSAFE_className={styles.search_parent}
                >
                  <Flex direction={'row'} alignSelf={'stretch'} height={'40px'} flexShrink={0} alignItems={'center'} justifyContent={'end'} gap={'8px'} UNSAFE_className={styles.search_field}>
                    <FilterSearchTextBox onSearchChange={reloadProjectActivities} filterParams={queryParams.current} serachedText={searchedValue} fpaTypeCategory={projectActivityValue.current} />
                    <i className={`${styles.icon_search} bi bi-search`}></i>
                  </Flex>
                </Flex>
                <Flex
                  direction={'row'}
                  position={'fixed'}
                  right={'16px'}
                  bottom={'95px'}
                  height={'48px'}
                  justifyContent={'center'}
                  alignItems={'center'}
                  gap={'8px'}
                  UNSAFE_className={styles.add_new_parent}
                >
                  <Link isQuiet onPress={onAddNew} UNSAFE_className={styles.add_new_btn}>
                    <i className="bi bi-plus fs-5">
                      <View UNSAFE_className={styles.add_new_btn_text}>{t('add_new', { ns: 'layout_components' })}</View>
                    </i>
                  </Link>
                </Flex>
              </View>
            </>
          )}
          {showMobileDetailView && (
            <>
              <View position={'relative'} width={'100%'} height={'64px'} overflow={'hidden'} UNSAFE_className={styles.layout_header_parent}>
                <View position={'absolute'} top={'calc(50% - 14px)'} left={'15%'} UNSAFE_className={styles.layout_header_text}>
                  {selectedItem?.item_type_name}
                </View>
                <View position={'absolute'} top={'calc(50% - 15px)'} left={'25px'} width={'24px'} height={'30px'} overflow={'hidden'} UNSAFE_className={styles.layout_header_btn_back}>
                  <Link
                    onPress={() => {
                      setStopLoadData(true);
                      setShowMobileDetailView(false);
                    }}
                  >
                    <i className="bi bi-chevron-left fs-5" />
                  </Link>
                </View>
                <a
                  onClick={() => {
                    setStopLoadData(true);
                    setShowMobileDetailView(false);
                  }}
                  className={styles.layout_header_btn_folder}
                >
                  <FinderIcon size={24} />
                </a>
              </View>
              <Flex direction="column" alignItems={'center'} justifyContent={'center'} UNSAFE_className={styles.detailsHolder} UNSAFE_style={{ padding: '20px', paddingTop: '0px' }}>
                {selectedItem && (
                  <>
                    <View UNSAFE_className={styles.service_status_bar} UNSAFE_style={{ backgroundColor: addStatusColor(selectedDataRow.customStateName), marginBottom: '5px' }}></View>
                    <ComponentFactory
                      selectedItem={selectedItem}
                      definition={definition}
                      systemAttributes={{
                        onPrint: item => console.log('onPrint', item),
                        onEdit: item => console.log('onEdit', item),
                        onDelete: item => console.log('onDelete', item),
                        onChat: item => console.log('onChat', item),
                        onConfig: (item, type) => console.log('onConfig', item, type),
                      }}
                    />
                  </>
                )}
              </Flex>
            </>
          )}
        </>
      ) : (
        <Grid
          areas={pageView == 'Full' ? ['filter header header', 'filter card detail'] : ['filter header header', 'filter detail detail']}
          columns={{ base: ['245px', '28%', '55%'], L: ['245px', '28%', '55%'] }}
          rows={['auto', '1fr']}
          height="100%"
          width="100%"
          UNSAFE_style={{ backgroundColor: '#f5f6f9', padding: '0px', margin: '0px', overflow: 'hidden' }}
        >
          <View gridArea="filter">
            <FilterComponent onFilterChange={reloadProjectActivities} filterParams={queryParams.current} filterStyle={filterStyle} />
          </View>
          <View gridArea="header" UNSAFE_style={pageView == 'Full' ? {} : { height: '100%' }}>
            <HeaderComponent
              onPageViewChange={handleViewChange}
              autoChanged={pageView}
              filterParams={queryParams.current}
              onSearchChange={reloadProjectActivities}
              fpaTypeCategory={projectActivityValue.current}
              onAddNew={onAddNew}
            />
          </View>
          {pageView == 'Full' ? (
            <>
              {showLoader ? (
                <Loader />
              ) : (
                <>
                  <View gridArea="card" UNSAFE_style={{ backgroundColor: '#f5f6f9' }}>
                    {rowsData?.length > 0 ? (
                      <ListBox
                        autoFocus
                        id="listProjectCards"
                        selectionMode="single"
                        onSelectionChange={handleListRowClick}
                        marginStart={'15px'}
                        minHeight={'130px'}
                        defaultSelectedKeys={selectedKeys}
                        aria-label="Project Activities Cards"
                        items={listProjectList.items}
                        isLoading={listProjectList.isLoading}
                        UNSAFE_style={cardStyle}
                        marginTop={'13px'}
                      >
                        {item => (
                          <Item key={item.id}>
                            <CardComponent item={item} projectStatusList={projectStatusList} />
                          </Item>
                        )}
                      </ListBox>
                    ) : (
                      <NoResultsMessage />
                    )}
                  </View>
                  <View
                    gridArea="detail"
                    backgroundColor={'static-white'}
                    borderRadius={'large'}
                    padding={'size-100'}
                    paddingTop={'0px'}
                    marginTop={'17px'}
                    UNSAFE_style={detailStyle}
                    width={'98%'}
                    UNSAFE_className={styles.detailView}
                  >
                    {definition &&
                      (showLoaderDetail ? (
                        <Loader />
                      ) : (
                        <View height="100%" UNSAFE_className={styles.detailsHolder} UNSAFE_style={{ overflow: 'hidden auto' }} marginStart={'10px'}>
                          {selectedItem && (
                            <>
                              {rowsData?.length > 0 ? (
                                <>
                                  <View UNSAFE_className={styles.service_status_bar} UNSAFE_style={{ backgroundColor: addStatusColor(selectedDataRow.customStateName) }}></View>
                                  <View height={'2px'}>&nbsp;</View>
                                  <ComponentFactory
                                    selectedItem={selectedItem}
                                    definition={definition}
                                    systemAttributes={{
                                      onPrint: item => console.log('onPrint', item),
                                      onEdit: item => console.log('onEdit', item),
                                      onDelete: item => console.log('onDelete', item),
                                      onChat: item => console.log('onChat', item),
                                      onConfig: (item, type) => console.log('onConfig', item, type),
                                    }}
                                  />
                                </>
                              ) : (
                                ''
                              )}
                            </>
                          )}
                        </View>
                      ))}
                  </View>
                </>
              )}
            </>
          ) : (
            <View gridArea="detail">
              {showLoader ? (
                <Loader />
              ) : (
                <View position={'relative'} overflow={'auto'} height={'100%'} width={'100%'} UNSAFE_className={styles.service_activities}>
                  {columnsData?.length > 0 && rowsData?.length > 0 ? (
                    <TableView
                      UNSAFE_className="tblLayoutComponent"
                      aria-label="Project Activities"
                      // onSelectionChange={handleTableRowClick}
                      onAction={handleTableRowClick}
                      selectionMode="single"
                      defaultSelectedKeys={selectedKeys}
                      selectionStyle="highlight"
                      width={{ base: '100%', L: '100%', M: '1100px' }}
                      minHeight={'100px'}
                      maxHeight={{ base: '100vh', L: '625px' }}
                    >
                      <TableHeader columns={columnsData}>
                        {(column: any) => (
                          <Column showDivider key={column.key}>
                            {column.name}
                          </Column>
                        )}
                      </TableHeader>
                      <TableBody items={listProjectList.items} loadingState={listProjectList.loadingState}>
                        {(item: any) => (
                          <Row>
                            {columnKey => (
                              <Cell>
                                {(() => {
                                  if (moment(item[columnKey], moment.ISO_8601, true).isValid()) {
                                    return moment(item[columnKey]).format('MM/DD/YYYY | hh:mm A');
                                  } else if (columnKey === 'customStateName') {
                                    return getStatusColor(item[columnKey]);
                                  } else {
                                    return item[columnKey];
                                  }
                                })()}
                              </Cell>
                            )}
                          </Row>
                        )}
                      </TableBody>
                    </TableView>
                  ) : (
                    <NoResultsMessage />
                  )}
                </View>
              )}
            </View>
          )}
        </Grid>
      )}
      <DataProvider>
        <DialogContainer onDismiss={handleAddNewCloseDismiss}>
          {showAdd && (
            <ContextNewDialog parentItem={selectedItem} onClose={handleAddNewClose} additionalMode={true} additionalData={{ fpaTypeCategory: projectActivityValue.current, fpaType: 'PROJECT' }} />
          )}
        </DialogContainer>
      </DataProvider>
    </>
  );
}
